import React, { useState, useEffect } from "react";
import { useLocation, useHistory, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { setAuthInfo, setLoginStatus, setUserInfo } from "../../store/reducers/auth-reducer";
import { Helmet } from "react-helmet";

const PASSWORD_RULES = [
  { id: "len", label: "At least 8 characters", test: (p) => p.length >= 8 },
  { id: "upper", label: "At least one uppercase letter (A-Z)", test: (p) => /[A-Z]/.test(p) },
  { id: "lower", label: "At least one lowercase letter (a-z)", test: (p) => /[a-z]/.test(p) },
  { id: "digit", label: "At least one number (0-9)", test: (p) => /[0-9]/.test(p) },
  { id: "special", label: "At least one special character (!@#$%^&*)", test: (p) => /[!@#$%^&*(),.?":{}|<>]/.test(p) },
];

const AcceptInvitation = () => {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();

  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [inviteData, setInviteData] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    // Parse token from query string ?token=... or path
    const searchParams = new URLSearchParams(location.search);
    let extractedToken = searchParams.get("token");

    if (!extractedToken && location.pathname.includes("/accept-invite/")) {
      extractedToken = location.pathname.split("/accept-invite/")[1];
    }

    if (!extractedToken) {
      setErrorMessage("No invitation token provided in the URL.");
      setLoading(false);
      return;
    }

    setToken(extractedToken);
    verifyToken(extractedToken);
  }, [location]);

  const verifyToken = async (tok) => {
    try {
      setLoading(true);
      const res = await axios.get(`auth/invitation/${tok}`);
      if (res.data && res.data.status) {
        setInviteData(res.data.data);
        if (res.data.data.name) {
          setName(res.data.data.name);
        }
      } else {
        setErrorMessage(res.data?.message || "Invalid or expired invitation token.");
      }
    } catch (err) {
      setErrorMessage(
        err.response?.data?.message ||
          "This invitation is invalid or has expired. Please ask your administrator to send a new invite."
      );
    } finally {
      setLoading(false);
    }
  };

  const isPasswordValid = PASSWORD_RULES.every((rule) => rule.test(password));
  const isMatch = password && password === confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isPasswordValid) {
      toast.error("Please meet all password requirements before submitting.");
      return;
    }

    if (!isMatch) {
      toast.error("Passwords do not match.");
      return;
    }

    try {
      setSubmitting(true);
      const res = await axios.post("auth/invitation/accept", {
        token,
        password,
        name: name.trim(),
      });

      if (res.data && res.data.status) {
        toast.success("Account activated successfully! Logging you in...", {
          autoClose: 2500,
        });

        const { member, tokens } = res.data.data;

        const authInfo = {
          expTime: tokens.expTime,
          id: member._id,
          token: tokens.token,
          refreshToken: tokens.refreshToken,
        };

        const userInfo = {
          name: member.name,
          email: member.email,
          role: member.role,
          organizationId: member.organizationId,
          organizationType: member.organizationType,
          isTeamMember: true,
        };

        localStorage.setItem("authInfo", JSON.stringify(authInfo));
        localStorage.setItem("userInfo", JSON.stringify(userInfo));
        localStorage.setItem("isLoggedIn", 1);

        dispatch(setLoginStatus({ isLoggedIn: true }));
        dispatch(setAuthInfo({ authInfo }));
        dispatch(setUserInfo({ userInfo }));

        setTimeout(() => {
          if (member.organizationType === "Seller") {
            history.replace("/seller/dashboard");
          } else {
            history.replace("/my-profile");
          }
        }, 1200);
      } else {
        toast.error(res.data?.message || "Could not accept invitation.");
        setSubmitting(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Activation failed.");
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#0f172a",
        backgroundImage: "radial-gradient(circle at 50% 20%, #1e293b 0%, #0f172a 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "2rem 1rem",
        fontFamily: "'Inter', sans-serif",
      }}
    >
      <Helmet>
        <title>Accept Invitation | GregPay Earth</title>
      </Helmet>

      <div
        className="card border-0 shadow-lg text-white"
        style={{
          maxWidth: "520px",
          width: "100%",
          borderRadius: "1rem",
          backgroundColor: "#1e293b",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
          overflow: "hidden",
        }}
      >
        {/* Header accent strip */}
        <div
          style={{
            height: "6px",
            background: "linear-gradient(90deg, #38bdf8 0%, #818cf8 50%, #c084fc 100%)",
          }}
        />

        <div className="card-body p-4 p-md-5">
          <div className="text-center mb-4">
            <h3 className="fw-bold text-white mb-2">Team Invitation</h3>
            <p className="text-white-50 small mb-0">
              Activate your sub-account with secure access credentials.
            </p>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-info" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3 text-white-50">Verifying your invitation token...</p>
            </div>
          ) : errorMessage ? (
            <div className="text-center py-4">
              <div
                className="d-inline-flex p-3 rounded-circle mb-3"
                style={{ backgroundColor: "rgba(239, 68, 68, 0.15)", color: "#f87171" }}
              >
                <i className="fa fa-exclamation-triangle fa-2x"></i>
              </div>
              <h5 className="text-danger fw-bold mb-2">Invitation Unavailable</h5>
              <p className="text-white-50 small mb-4">{errorMessage}</p>
              <Link to="/" className="btn btn-outline-light btn-sm px-4">
                Return to Home
              </Link>
            </div>
          ) : (
            inviteData && (
              <form onSubmit={handleSubmit}>
                {/* Organization Details Card */}
                <div
                  className="p-3 rounded mb-4 border"
                  style={{
                    backgroundColor: "rgba(15, 23, 42, 0.6)",
                    borderColor: "rgba(255, 255, 255, 0.1)",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span className="text-white-50 small">Organization:</span>
                    <span className="fw-semibold text-white">
                      {inviteData.organizationName}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span className="text-white-50 small">Account Type:</span>
                    <span className="badge bg-secondary">
                      {inviteData.organizationType === "Seller"
                        ? "Seller Store Staff"
                        : "Corporate Buyer Staff"}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span className="text-white-50 small">Assigned Role:</span>
                    <span className="badge bg-info text-dark fw-bold">
                      {inviteData.role}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="text-white-50 small">Email Address:</span>
                    <span className="text-info small font-monospace">
                      {inviteData.email}
                    </span>
                  </div>
                </div>

                {/* Name Field */}
                <div className="mb-3">
                  <label className="form-label text-white-50 small">Full Name</label>
                  <input
                    type="text"
                    className="form-control bg-dark text-white border-secondary"
                    placeholder="Enter your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>

                {/* Password Field */}
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <label className="form-label text-white-50 small mb-0">Create Password</label>
                    <button
                      type="button"
                      className="btn btn-link btn-sm text-info p-0 text-decoration-none"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? "Hide" : "Show"}
                    </button>
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    className="form-control bg-dark text-white border-secondary"
                    placeholder="Enter strong password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>

                {/* Confirm Password Field */}
                <div className="mb-3">
                  <label className="form-label text-white-50 small">Confirm Password</label>
                  <input
                    type={showPassword ? "text" : "password"}
                    className="form-control bg-dark text-white border-secondary"
                    placeholder="Re-enter password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                  {confirmPassword && (
                    <div className="mt-1 small">
                      {isMatch ? (
                        <span className="text-success">
                          <i className="fa fa-check me-1"></i> Passwords match
                        </span>
                      ) : (
                        <span className="text-danger">
                          <i className="fa fa-times me-1"></i> Passwords do not match
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Complexity Criteria Checklist */}
                <div
                  className="p-3 rounded mb-4"
                  style={{
                    backgroundColor: "rgba(15, 23, 42, 0.4)",
                    border: "1px solid rgba(255, 255, 255, 0.08)",
                  }}
                >
                  <div className="text-white-50 small fw-bold mb-2">Password Requirements:</div>
                  <div className="row g-1">
                    {PASSWORD_RULES.map((rule) => {
                      const passed = rule.test(password);
                      return (
                        <div key={rule.id} className="col-12 small d-flex align-items-center">
                          <i
                            className={`fa ${
                              passed ? "fa-check-circle text-success" : "fa-circle-o text-muted"
                            } me-2`}
                          ></i>
                          <span className={passed ? "text-white" : "text-white-50"}>
                            {rule.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={!isPasswordValid || !isMatch || submitting}
                  className="btn btn-primary w-100 py-2 fw-bold shadow-sm"
                  style={{
                    background:
                      isPasswordValid && isMatch
                        ? "linear-gradient(135deg, #0284c7 0%, #2563eb 100%)"
                        : "#334155",
                    borderColor: "transparent",
                  }}
                >
                  {submitting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Activating Account...
                    </>
                  ) : (
                    "Activate Account & Join Team"
                  )}
                </button>
              </form>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default AcceptInvitation;
